import {
	PrimaryButton,
	Input,
	Typography,
	Row,
	ModalSection,
} from '@copyrightagent/basic-components'
import validator from 'validator'
import { Button, CircularProgress } from '@material-ui/core'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ImageRow from './ImageRow'
import { useStyle } from '../style'

interface Image {
	image_id: string
	client_image_id: string
	photographer_name?: string
	photographer_id?: string
	supplier?: string
	thumbnail_url?: string | null
	preview_url?: string | null
}

interface SearchImagesStepProps {
	handleClose: () => void
	handleSubmit: (url: string, imageId: string) => void
	onSearch: (searchTerm: string) => Promise<Image[]>
	isPending: boolean
}

const validateAndSanitizeURL = (input: string): string | null => {
	const sanitizedInput = input.trim()

	const isUrl = validator.isURL(input, {
		require_protocol: false,
		require_tld: true,
		protocols: ['http', 'https'],
		allow_underscores: true,
	})

	if (!isUrl) {
		return null
	}
	return sanitizedInput
}

const SearchImagesStep = ({
	handleClose,
	handleSubmit,
	onSearch,
	isPending,
}: SearchImagesStepProps): ReactElement => {
	const classes = useStyle()
	const { t } = useTranslation()
	const [caseUrl, setCaseUrl] = useState<string>('')
	const [validUrl, setValidUrl] = useState<boolean>(true)
	const [searchTerm, setSearchTerm] = useState('')
	const [images, setImages] = useState<Image[]>([])
	const [selectedImage, setSelectedImage] = useState<string | null>(null)
	const [isSearching, setIsSearching] = useState(false)
	const [hasSearched, setHasSearched] = useState(false)

	const handleSearch = async () => {
		if (!searchTerm.trim()) return

		setIsSearching(true)
		setHasSearched(true)
		try {
			const results = await onSearch(searchTerm)
			setImages(results)
		} catch (error) {
			console.error('Failed to search images:', error)
			setImages([])
		} finally {
			setIsSearching(false)
		}
	}

	const handleImageSelect = (imageId: string) => {
		setSelectedImage(imageId)
	}

	const handleFormSubmit = () => {
		const url = validateAndSanitizeURL(caseUrl)

		const isValidUrl = !!url
		setValidUrl(isValidUrl)
		if (selectedImage && isValidUrl) {
			handleSubmit(caseUrl, selectedImage)
		}
	}

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSearch()
		}
	}

	return (
		<>
			<ModalSection>
				<Typography variant="body2" className={classes.actionLabel}>
					Case URL
				</Typography>
				<Input
					value={caseUrl}
					type="text"
					label=""
					className={classes.input}
					variant="filled"
					onChange={(e) => {
						setCaseUrl(e.target.value)
					}}
					placeholder={'Enter the URL where you found the violation'}
					disabled={isPending}
					fullWidth
				/>
				{!validUrl && (
					<Typography variant="body2" color="error">
						Invalid URL
					</Typography>
				)}
			</ModalSection>

			<ModalSection>
				<Typography variant="body2" className={classes.actionLabel}>
					Search for Image
				</Typography>
				<div
					style={{
						display: 'flex',
						gap: '10px',
						alignItems: 'flex-end',
						marginBottom: '20px',
					}}
				>
					<div style={{ flex: 1 }}>
						<Input
							label=""
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							onKeyPress={handleKeyPress}
							placeholder={'Enter image ID to search...'}
							disabled={isSearching || isPending}
							fullWidth
						/>
					</div>
					<Button
						variant="contained"
						color="primary"
						onClick={handleSearch}
						disabled={isSearching || isPending || !searchTerm.trim()}
					>
						{isSearching ? <CircularProgress size={24} /> : 'Search'}
					</Button>
				</div>

				{hasSearched && (
					<>
						{images.length > 0 ? (
							<div
								style={{
									maxHeight: '400px',
									overflowY: 'auto',
									padding: '10px 0',
								}}
							>
								{images.map((image) => (
									<ImageRow
										key={image.image_id}
										image={image}
										isSelected={selectedImage === image.image_id}
										onSelect={handleImageSelect}
									/>
								))}
							</div>
						) : (
							<div
								style={{ textAlign: 'center', padding: '40px', color: '#999' }}
							>
								<Typography variant="body1">
									No images found. Try a different search term.
								</Typography>
							</div>
						)}
					</>
				)}
			</ModalSection>

			<ModalSection>
				<Row justify="flex-end">
					<Button onClick={handleClose} disabled={isPending}>
						<Typography>{t('cancel')}</Typography>
					</Button>
					<PrimaryButton
						onClick={handleFormSubmit}
						disabled={!selectedImage || isPending}
						loading={isPending}
						text={'Submit Case'}
						loadingText={'Submitting Case...'}
					/>
				</Row>
			</ModalSection>
		</>
	)
}

export default SearchImagesStep
